import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import {
  DatabaseZap, Download, AlertTriangle, CheckCircle2, Loader2,
  GitBranch, ListTree, ShieldAlert, FlaskConical, Building2, MapPin,
} from 'lucide-react';

const EnvBadge = ({ env }) => {
  const map = {
    PREVIEW: { bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-300', label: 'PREVIEW' },
    LIVE: { bg: 'bg-rose-100', text: 'text-rose-800', ring: 'ring-rose-300', label: 'LIVE (PRODUCTION)' },
    UNKNOWN: { bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-300', label: 'UNKNOWN' },
  };
  const s = map[env] || map.UNKNOWN;
  return (
    <span data-testid="account-migration-env-badge"
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-extrabold uppercase tracking-wide ring-2 ${s.bg} ${s.text} ${s.ring}`}>
      <DatabaseZap size={15} /> {s.label}
    </span>
  );
};

const Stat = ({ label, value, tone = 'slate', testid }) => (
  <div data-testid={testid} className={`rounded-xl border p-3 ${tone === 'rose' ? 'border-rose-200 bg-rose-50' : tone === 'amber' ? 'border-amber-200 bg-amber-50' : tone === 'green' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-0.5 text-2xl font-extrabold text-[#0E2A47]">{value}</div>
  </div>
);

const Section = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
    <div className="mb-3 flex items-center gap-2">
      <Icon size={18} className="text-[#0E2A47]" />
      <h3 className="text-base font-bold text-[#0E2A47]">{title}</h3>
    </div>
    {children}
  </div>
);

const AccountMigrationDryRun = () => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const run = async () => {
    setLoading(true); setError(''); setReport(null);
    try {
      const r = await axios.get(`${API}/admin/account-migration/dry-run`, authHeaders);
      setReport(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to run the dry run.');
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/account-migration/dry-run/download`, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      const env = report?.environment?.environment || 'ENV';
      a.href = url; a.download = `account-migration-dryrun-${env}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const s = report?.summary || {};

  return (
    <div className="space-y-5" data-testid="account-migration-view">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-[#0E2A47] sm:text-2xl">Account Migration — Dry Run</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              Read-only inspection of the account-number migration against <strong>this environment’s database</strong>.
              It shows existing vs proposed numbers, the customer/subaccount/location hierarchy, every linked record
              it would touch across all modules, and anything needing review. It never changes records, reserves
              numbers, or applies the migration.
            </p>
          </div>
          <button onClick={run} disabled={loading} data-testid="account-migration-run"
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15395f] disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <DatabaseZap size={16} />}
            {loading ? 'Inspecting…' : 'Run Dry Run'}
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <ShieldAlert size={14} className="text-amber-600" />
          Safe to run anytime — no Apply action exists here. Applying to Live remains a separate, deliberate step.
        </div>
      </div>

      {error && (
        <div data-testid="account-migration-error" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {report && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3">
              <EnvBadge env={report.environment?.environment} />
              <span className="text-xs text-slate-500" data-testid="account-migration-env-detail">
                DB <strong className="text-slate-700">{report.environment?.database}</strong> · host {report.environment?.host}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 size={13} /> Read-only · nothing changed
              </span>
              <button onClick={download} disabled={downloading} data-testid="account-migration-download"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download mapping
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="account-migration-summary">
            <Stat testid="stat-customers" label="Customers changed" value={s.customers_changed ?? 0} tone={s.customers_changed ? 'amber' : 'green'} />
            <Stat testid="stat-locations" label="Locations changed" value={s.locations_changed ?? 0} tone={s.locations_changed ? 'amber' : 'green'} />
            <Stat testid="stat-converted" label="Legacy → converted" value={`${s.legacy_locations_converted ?? 0}/${s.legacy_locations_now ?? 0}`} />
            <Stat testid="stat-linked" label="Linked records affected" value={s.linked_records_affected ?? 0} />
            <Stat testid="stat-conflicts" label="Conflicts" value={s.conflicts ?? 0} tone={s.conflicts ? 'rose' : 'green'} />
            <Stat testid="stat-review" label="Exceptions after" value={s.review_items ?? 0} tone={s.review_items ? 'amber' : 'green'} />
          </div>

          {(s.customers_changed === 0 && s.locations_changed === 0) && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="account-migration-nochange">
              <CheckCircle2 size={15} className="mr-1 inline" /> This environment’s database already matches the target structure — the migration would make no changes here (idempotent).
            </div>
          )}

          <Section icon={ListTree} title="Proposed customer / subaccount / location hierarchy" testid="account-migration-relationships">
            <div className="space-y-2">
              {report.relationships.map((c) => (
                <div key={c.account_number || c.name} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Building2 size={14} className="text-[#0E2A47]" />
                    <span className="font-semibold text-[#0E2A47]">{c.name}</span>
                    <span className="rounded-full bg-[#0E2A47] px-2 py-0.5 font-mono text-[10px] font-semibold text-white">{c.account_number || '—'}</span>
                    {c.direct_locations > 0 && <span className="text-xs text-slate-500">{c.direct_locations} direct location{c.direct_locations === 1 ? '' : 's'}</span>}
                  </div>
                  {c.subaccounts?.length > 0 && (
                    <div className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3">
                      {c.subaccounts.map((sub) => (
                        <div key={sub.account_number} className="flex flex-wrap items-center gap-2 text-sm">
                          <GitBranch size={12} className="text-[#8FC93A]" />
                          <span className="text-slate-700">{sub.name}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-600">{sub.account_number}</span>
                          <span className="text-xs text-slate-400">{sub.locations} location{sub.locations === 1 ? '' : 's'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {(report.planned_changes.customers.length > 0 || report.planned_changes.locations.length > 0) && (
            <Section icon={MapPin} title="Existing → proposed account numbers" testid="account-migration-changes">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Existing</th><th className="py-2 pr-3">Proposed</th><th className="py-2 pr-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.planned_changes.customers.map((r, i) => (
                      <tr key={`c${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 text-slate-400">{r.created ? 'Customer (new)' : 'Customer'}</td>
                        <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                        <td className="py-2 pr-3 font-mono text-slate-500">{r.old || '—'}</td>
                        <td className="py-2 pr-3 font-mono font-semibold text-[#0E2A47]">{r.new}</td>
                        <td className="py-2 pr-3 text-xs text-slate-500">{r.note || (r.becomes_subaccount_of ? `→ subaccount of ${r.becomes_subaccount_of}` : (r.changes || []).join(', '))}</td>
                      </tr>
                    ))}
                    {report.planned_changes.locations.map((r, i) => (
                      <tr key={`l${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 text-slate-400">{r.created ? 'Location (new)' : 'Location'}</td>
                        <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                        <td className="py-2 pr-3 font-mono text-slate-500">{r.old || '—'}</td>
                        <td className="py-2 pr-3 font-mono font-semibold text-[#0E2A47]">{r.new}</td>
                        <td className="py-2 pr-3 text-xs text-slate-500">{r.note || (r.changes || []).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Section icon={DatabaseZap} title="Affected linked records by module" testid="account-migration-impact">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {report.linked_record_impact.map((m) => (
                <div key={m.module} className={`rounded-lg border p-3 ${m.affected_records ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="text-sm font-semibold text-[#0E2A47]">{m.module}</div>
                  <div className="text-2xl font-extrabold text-[#0E2A47]">{m.affected_records}</div>
                  {m.sources?.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500">{m.sources.map((x) => `${x.collection}: ${x.records}`).join(' · ')}</div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">Counts show records linked to the customers/locations being changed — each is read by its permanent ID, so numbers/relationships update in place.</p>
          </Section>

          {(report.conflicts.length > 0 || report.review_items.length > 0) ? (
            <Section icon={ShieldAlert} title="Conflicts & items requiring review" testid="account-migration-review">
              {report.conflicts.map((c, i) => (
                <div key={`cf${i}`} className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                  <strong>Conflict:</strong> {c.type} {c.account_number ? `(${c.account_number})` : ''} {c.count ? `· ${c.count}` : ''}
                </div>
              ))}
              {report.review_items.map((r, i) => (
                <div key={`rv${i}`} className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <strong>Review ({r.count}):</strong> {r.detail}
                </div>
              ))}
            </Section>
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="account-migration-clean">
              <CheckCircle2 size={16} /> No conflicts and nothing flagged for review.
            </div>
          )}

          {report.planned_changes.notes?.length > 0 && (
            <Section icon={FlaskConical} title="Migration notes" testid="account-migration-notes">
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                {report.planned_changes.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default AccountMigrationDryRun;
